import React from 'react';
import IncrementInput from '../../../../shared/components/IncrementInput';
import { getPatternRepeatInfo, getValidTargetStitches, calculateRepeatsToTarget, calculateTargetRows, canAddExtraRowForSide } from '../../../../shared/utils/targetStitchUtils';
import { getCurrentSide } from '../../../../shared/utils/sideIntelligence';

const DurationChoice = ({
  wizardData,
  updateWizardData,
  construction,
  project,
  mode,
  onSave,
  onCancel,
  showSaveActions = false,
  currentStitches = 0
}) => {



  const { pattern } = wizardData.stitchPattern;

  // SAFETY CHECK: Ensure duration exists
  if (!wizardData.duration) {
    console.log('Missing duration in wizardData:', wizardData);
    return <div>Loading...</div>;
  }

  // Skip duration for Cast On (already configured)
  if (pattern === 'Cast On') {
    return (
      <div className="stack-lg">
        <div className="success-block-center">
          <div className="text-2xl mb-2">🗝️</div>
          <h3 className="content-header-secondary mb-2">Cast On Ready!</h3>
          <p className="text-sm text-sage-600">Your cast on step is ready to add</p>
        </div>
      </div>
    );
  }

  const isEditMode = mode === 'edit';

  // Check if pattern has repeats (existing code)
  const patternHasRepeats = wizardData.stitchPattern?.rowsInPattern &&
    parseInt(wizardData.stitchPattern.rowsInPattern) > 0;

  // ✅ NEW: Check if pattern supports target-based repeats
  const repeatInfo = getPatternRepeatInfo(wizardData.stitchPattern);
  const supportsTargetRepeats = repeatInfo.hasRepeat && repeatInfo.stitchChangePerRepeat !== 0;

  // ✅ NEW: Calculate valid target stitches if applicable
  const validTargets = supportsTargetRepeats
    ? getValidTargetStitches(currentStitches, wizardData.stitchPattern, 500)
    : [];

  // ✅ NEW: Check if color pattern exists
  const hasColorPattern = wizardData.colorwork?.type === 'stripes' &&
    wizardData.colorwork?.stripeSequence?.length > 0;

  const handleDurationTypeSelect = (type) => {
    const defaultValue = (type === 'repeats' || type === 'color_repeats') ? '1' : '';

    // Set default target to first complete repeat
    if (type === 'target_repeats' && validTargets.length > 0) {
      const rowsInPattern = parseInt(wizardData.stitchPattern.rowsInPattern) || 0;
      // Find the target at the end of the first complete repeat
      const defaultTarget = currentStitches + repeatInfo.stitchChangePerRepeat;
      updateWizardData('duration', { type, value: defaultValue, targetStitches: defaultTarget });
    } else {
      updateWizardData('duration', { type, value: defaultValue });
    }
  };

  // Smart gauge calculation using real project data
  const calculateRowsFromLength = (inches) => {
    const projectGauge = project?.gauge;

    if (!inches || !projectGauge?.rowGauge?.rows || !projectGauge?.rowGauge?.measurement) {
      return null;
    }

    // Handle unit conversion if needed
    const inputUnits = wizardData.duration?.units || 'inches';
    const gaugeUnits = project?.defaultUnits || 'inches';

    let convertedInches = parseFloat(inches);
    if (inputUnits === 'cm' && gaugeUnits === 'inches') {
      convertedInches = convertedInches / 2.54; // cm to inches
    } else if (inputUnits === 'inches' && gaugeUnits === 'cm') {
      convertedInches = convertedInches * 2.54; // inches to cm
    }

    const rowsPerUnit = projectGauge.rowGauge.rows / projectGauge.rowGauge.measurement;
    return Math.round(convertedInches * rowsPerUnit);
  };

  const estimatedRows = wizardData.duration.type === 'length' && wizardData.duration.value
    ? calculateRowsFromLength(wizardData.duration.value)
    : null;

  // ✅ NEW: Calculate target repeat details
  const targetRepeatDetails = wizardData.duration.type === 'target_repeats' && wizardData.duration.targetStitches
    ? (() => {
      const targetStitches = parseInt(wizardData.duration.targetStitches);
      const calculation = calculateRepeatsToTarget(
        currentStitches,
        targetStitches,
        repeatInfo.stitchChangePerRepeat
      );

      const rowCalc = calculateTargetRows(
        calculation.repeats,
        repeatInfo.rowsInPattern,
        wizardData.duration.completeSequence || false,
        targetStitches,
        currentStitches,
        repeatInfo.stitchChangePerRepeat,
        wizardData.stitchPattern.customSequence?.rows || null  // ✅ Pass actual row data
      );

      return {
        ...calculation,
        ...rowCalc,
        changePerRepeat: repeatInfo.stitchChangePerRepeat,
        rowsPerRepeat: repeatInfo.rowsInPattern
      };
    })()
    : null;

  // ✅ NEW: Check if we can offer "end on side" option
  const endOnSideOption = targetRepeatDetails && !wizardData.duration.completeSequence
    ? (() => {
      const rows = wizardData.stitchPattern.customSequence?.rows;
      if (!rows) return null;

      const check = canAddExtraRowForSide(rows, targetRepeatDetails.reachedOnRow);
      if (!check.canAddRow) return null;

      // Calculate what side we'd end on naturally
      const naturalEndingSide = construction === 'round'
        ? (targetRepeatDetails.reachedOnRow % 2 === 0 ? 'even' : 'odd')
        : getCurrentSide(construction, targetRepeatDetails.reachedOnRow, 'RS');

      return {
        available: true,
        naturalEndingSide,
        options: construction === 'round'
          ? ['even', 'odd']
          : ['RS', 'WS']
      };
    })()
    : null;

  const canSave = () => {
    if (wizardData.duration.type === 'target_repeats') {
      return wizardData.duration.targetStitches && parseInt(wizardData.duration.targetStitches) > 0;
    }
    return wizardData.duration?.type && wizardData.duration?.value && (
      wizardData.duration.type === 'until_length' ? true : parseFloat(wizardData.duration.value) > 0
    );
  };

  // Save handlers for edit mode
  const handleSave = () => {
    if (onSave && canSave()) {
      onSave(wizardData.duration);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Validate and correct target stitches on blur
  const handleTargetStitchesBlur = () => {
    if (wizardData.duration.type === 'target_repeats' && wizardData.duration.targetStitches) {
      const enteredValue = parseInt(wizardData.duration.targetStitches);
      const stitchChangePerRepeat = repeatInfo.stitchChangePerRepeat;

      if (!isNaN(enteredValue) && stitchChangePerRepeat !== 0) {
        const stitchDifference = enteredValue - currentStitches;

        // Calculate nearest valid target
        const repeats = Math.round(stitchDifference / stitchChangePerRepeat);
        const nearest = currentStitches + (repeats * stitchChangePerRepeat);

        // Snap to nearest valid value
        if (nearest !== enteredValue) {
          updateWizardData('duration', { targetStitches: nearest });
        }
      }
    }
  };

  return (
    <div className="stack-lg">
      {/* Mode indicator for edit mode */}
      {isEditMode && (
        <div className="bg-yarn-100 border-2 border-yarn-200 rounded-xl p-3 mb-4">
          <p className="text-sm text-yarn-600 font-medium">
            🔧 Edit Mode - Duration Configuration
          </p>
          <p className="text-xs text-yarn-500 mt-1">
            Update how you want to measure your {pattern?.toLowerCase()}
          </p>
        </div>
      )}

      <div>
        <h2 className="content-header-primary">Configure Length</h2>
        <p className="content-subheader">Choose how you want to measure your {pattern?.toLowerCase()}</p>
      </div>

      {/* Bind Off - Special case */}
      {pattern === 'Bind Off' ? (
        <div className="space-y-4">
          <div className="success-block">
            <div className="text-center">
              <div className="text-2xl mb-2">✂️</div>
              <h3 className="content-header-secondary mb-2">Bind Off Stitches</h3>
              <p className="text-sm text-sage-600">Specify how many stitches to bind off</p>
            </div>
          </div>

          <div>
            <label className="form-label">
              Number of Stitches to Bind Off
            </label>
            <input
              type="number"
              value={wizardData.duration.value}
              onChange={(e) => updateWizardData('duration', { value: e.target.value, type: 'stitches' })}
              placeholder="Leave blank for all stitches"
              className="w-full border-2 border-wool-200 rounded-xl px-4 py-4 text-base focus:border-sage-500 focus:ring-0 transition-colors placeholder-wool-400 bg-white"
            />
            <p className="text-xs text-wool-500 mt-2">
              Leave blank to bind off all remaining stitches
            </p>
          </div>
        </div>
      ) : (
        /* Normal Duration Selection - Single Page with Radio Buttons */
        <div className="space-y-4">

          {/* Rows Option */}
          <label className={`block cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${wizardData.duration.type === 'rows'
            ? 'border-sage-500 bg-sage-100 text-sage-700 shadow-sm'
            : 'border-wool-200 bg-white text-wool-700 hover:border-sage-300 hover:bg-sage-50'
            }`}>
            <div className="flex items-start gap-4">
              <input
                type="radio"
                name="duration_type"
                value="rows"
                checked={wizardData.duration.type === 'rows'}
                onChange={() => handleDurationTypeSelect('rows')}
                className="w-4 h-4 text-sage-600 mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-2xl">📊</div>
                  <div className="text-left">
                    <div className="font-semibold text-base">{construction === 'round' ? 'Rounds' : 'Rows'}</div>
                    <div className="text-sm opacity-75">Count specific number of {construction === 'round' ? 'rounds' : 'rows'}</div>
                  </div>
                </div>

                {wizardData.duration.type === 'rows' && (
                  <div className="mt-3 space-y-2">
                    <IncrementInput
                      value={wizardData.duration.value}
                      onChange={(value) => updateWizardData('duration', { value })}
                      label="number of rows"
                      unit="rows"
                      construction={construction}
                      min={1}
                      size="sm"
                    />

                    <div className="text-xs text-sage-600">
                      💡 This is the total number of {construction === 'round' ? 'rounds' : 'rows'} to work for this section
                    </div>
                  </div>
                )}
              </div>
            </div>
          </label>

          {/* Length from current position */}
          <label className={`block cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${wizardData.duration.type === 'length'
            ? 'border-sage-500 bg-sage-100 text-sage-700 shadow-sm'
            : 'border-wool-200 bg-white text-wool-700 hover:border-sage-300 hover:bg-sage-50'
            }`}>
            <div className="flex items-start gap-4">
              <input
                type="radio"
                name="duration_type"
                value="length"
                checked={wizardData.duration.type === 'length'}
                onChange={() => handleDurationTypeSelect('length')}
                className="w-4 h-4 text-sage-600 mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-2xl">📏</div>
                  <div className="text-left">
                    <div className="font-semibold text-base">Length from current position</div>
                    <div className="text-sm opacity-75">Add specific length from where you are now</div>
                  </div>
                </div>

                {wizardData.duration.type === 'length' && (
                  <div className="mt-3 stack-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-sage-700">Add</span>
                      <IncrementInput
                        value={wizardData.duration.value}
                        onChange={(value) => updateWizardData('duration', { value })}
                        label="length to add"
                        min={0.25}
                        useDecimals={true}
                        step={0.25}
                        size="sm"
                      />
                      <span className="text-sm text-sage-700">
                        {project?.defaultUnits || 'inches'}
                      </span>
                    </div>

                    {/* Smart gauge field */}
                    {estimatedRows && (
                      <div className="bg-sage-50 border border-sage-200 rounded-lg p-3">
                        <div className="text-sm text-sage-700">
                          <span className="font-medium">Estimated rows:</span> {estimatedRows}
                          <div className="text-xs text-sage-600 mt-1">
                            Using gauge: {project?.gauge?.rowGauge?.rows || '24'} rows = {project?.gauge?.rowGauge?.measurement || '4'} {project?.defaultUnits || 'inches'}
                            {project?.gauge?.pattern && ` in ${project.gauge.pattern}`}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-sage-600">
                      💡 This adds length from your current position. Gauge calculation helps estimate rows needed
                    </div>
                  </div>
                )}

              </div>
            </div>
          </label>

          {/* Length until target */}
          {mode !== 'notepad' && (
            <label className={`block cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${wizardData.duration.type === 'until_length'
              ? 'border-sage-500 bg-sage-100 text-sage-700 shadow-sm'
              : 'border-wool-200 bg-white text-wool-700 hover:border-sage-300 hover:bg-sage-50'
              }`}>
              <div className="flex items-start gap-4">
                <input
                  type="radio"
                  name="duration_type"
                  value="until_length"
                  checked={wizardData.duration.type === 'until_length'}
                  onChange={() => handleDurationTypeSelect('until_length')}
                  className="w-4 h-4 text-sage-600 mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-2xl">📐</div>
                    <div className="text-left">
                      <div className="font-semibold text-base">Length until target</div>
                      <div className="text-sm opacity-75">Work until piece measures a specified length</div>
                    </div>
                  </div>

                  {wizardData.duration.type === 'until_length' && (
                    <div className="mt-3 space-y-3">
                      <div className="flex items-center gap-2 text-sm text-sage-700">
                        <span>Work until piece measures</span>
                        <IncrementInput
                          value={wizardData.duration.value}
                          onChange={(value) => updateWizardData('duration', { value })}
                          label="target measurement"
                          min={0.25}
                          useDecimals={true}
                          step={0.25}
                          size="sm"
                        />
                        <span>{wizardData.duration.units || project?.defaultUnits || 'inches'}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <label className="text-sage-700">from</label>
                        <input
                          type="text"
                          value={wizardData.duration.reference || ''}
                          onChange={(e) => updateWizardData('duration', { reference: e.target.value })}
                          placeholder="cast on, start of armhole, etc."
                          className="flex-1 border-2 border-sage-300 rounded-lg px-3 py-2 text-sm focus:border-sage-500 focus:ring-0 transition-colors"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </label>
          )}

          {/* Pattern repeats - only show if pattern has repeats */}
          {patternHasRepeats && (
            <label className={`block cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${wizardData.duration.type === 'repeats'
              ? 'border-sage-500 bg-sage-100 text-sage-700 shadow-sm'
              : 'border-wool-200 bg-white text-wool-700 hover:border-sage-300 hover:bg-sage-50'
              }`}>
              <div className="flex items-start gap-4">
                <input
                  type="radio"
                  name="duration_type"
                  value="repeats"
                  checked={wizardData.duration.type === 'repeats'}
                  onChange={() => handleDurationTypeSelect('repeats')}
                  className="w-4 h-4 text-sage-600 mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-2xl">🔄</div>
                    <div className="text-left">
                      <div className="font-semibold text-base">Pattern repeats</div>
                      <div className="text-sm opacity-75">Repeat the {wizardData.stitchPattern.rowsInPattern}-{construction === 'round' ? 'round' : 'row'} pattern</div>
                    </div>
                  </div>

                  {wizardData.duration.type === 'repeats' && (
                    <div className="mt-3 space-y-2">

                      <div className="flex items-center gap-2">
                        <span className="text-sm text-sage-700">Work the pattern</span>
                        <IncrementInput
                          value={wizardData.duration.value}
                          onChange={(value) => updateWizardData('duration', { value })}
                          label="pattern repeats"
                          unit="times"
                          min={1}
                          size="sm"
                        />
                      </div>

                      {wizardData.duration.value && (
                        <div className="text-xs text-sage-600 bg-sage-50 rounded-lg p-2">
                          <strong>Preview:</strong> Work the {wizardData.stitchPattern.rowsInPattern}-{construction === 'round' ? 'round' : 'row'} pattern {wizardData.duration.value} times
                          ({(parseInt(wizardData.stitchPattern.rowsInPattern) || 0) * (parseInt(wizardData.duration.value) || 0)} total {construction === 'round' ? 'rounds' : 'rows'})
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </label>
          )}

          {/* ✅ NEW: Target Stitch Count - only show for patterns with net stitch change */}
          {supportsTargetRepeats && (
            <label className={`block cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${wizardData.duration.type === 'target_repeats'
              ? 'border-sage-500 bg-sage-100 text-sage-700 shadow-sm'
              : 'border-wool-200 bg-white text-wool-700 hover:border-sage-300 hover:bg-sage-50'
              }`}>
              <div className="flex items-start gap-4">
                <input
                  type="radio"
                  name="duration_type"
                  value="target_repeats"
                  checked={wizardData.duration.type === 'target_repeats'}
                  onChange={() => handleDurationTypeSelect('target_repeats')}
                  className="w-4 h-4 text-sage-600 mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-2xl">🎯</div>
                    <div className="text-left">
                      <div className="font-semibold text-base">Until target stitch count</div>
                      <div className="text-sm opacity-75">Repeat pattern until reaching specific stitch count</div>
                    </div>
                  </div>

                  {wizardData.duration.type === 'target_repeats' && (
                    <div className="mt-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-sage-700">Target stitches:</span>
                        <IncrementInput
                          value={wizardData.duration.targetStitches}
                          onChange={(value) => updateWizardData('duration', { targetStitches: value })}
                          onBlur={() => {
                            const entered = parseInt(wizardData.duration.targetStitches);
                            if (!isNaN(entered) && validTargets.length > 0) {
                              const nearest = validTargets.reduce((prev, curr) =>
                                Math.abs(curr - entered) < Math.abs(prev - entered) ? curr : prev
                              );
                              if (nearest !== entered) {
                                updateWizardData('duration', { targetStitches: nearest });
                              }
                            }
                          }}
                          label="target stitch count"
                          unit="stitches"
                          min={Math.min(...validTargets) || 1}
                          max={Math.max(...validTargets) || (currentStitches * 10)}
                          size="sm"
                        />
                      </div>

                      <div className="text-xs text-sage-600 bg-white border border-sage-200 rounded-lg p-2">
                        <div><strong>Currently:</strong> {currentStitches} stitches</div>
                        <div><strong>Pattern change:</strong> {repeatInfo.stitchChangePerRepeat > 0 ? '+' : ''}{repeatInfo.stitchChangePerRepeat} stitches per repeat</div>
                      </div>

                      {targetRepeatDetails && (
                        <>
                          <div className="bg-sage-50 border border-sage-200 rounded-lg p-3">
                            <div className="text-sm text-sage-700">
                              <div className="font-medium mb-1">Calculation:</div>
                              <div className="text-xs space-y-1">
                                <div>• Needs {targetRepeatDetails.repeats} {targetRepeatDetails.repeats === 1 ? 'repeat' : 'repeats'}</div>
                                <div>• Total {construction === 'round' ? 'rounds' : 'rows'}: {targetRepeatDetails.totalRows}</div>
                                <div>• Ending stitches: {targetRepeatDetails.endingStitches}</div>
                              </div>
                            </div>
                          </div>

                          {/* ✅ REFACTORED: Single "How to end" section */}
                          <div className="bg-sage-50 border border-sage-200 rounded-lg p-3">
                            <div className="text-sm font-medium text-sage-900 mb-2">
                              How to end this step
                            </div>
                            <div className="text-xs text-sage-600 mb-3">
                              {endOnSideOption
                                ? `Target is reached on ${construction === 'round' ? 'an' : ''} ${endOnSideOption.naturalEndingSide} ${construction === 'round' ? 'round' : ''}`
                                : `Choose how to complete this step`
                              }
                            </div>

                            <div className="space-y-2">
                              {/* Option 1: Stop at target (default) */}
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="endingBehavior"
                                  checked={!wizardData.duration.completeSequence && !wizardData.duration.endOnSide}
                                  onChange={() => updateWizardData('duration', { completeSequence: false, endOnSide: null })}
                                  className="w-4 h-4 text-sage-600"
                                />
                                <span className="text-sm text-sage-700">
                                  Stop at target
                                </span>
                              </label>

                              {/* Option 2: Complete final repeat */}
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="endingBehavior"
                                  checked={wizardData.duration.completeSequence === true}
                                  onChange={() => updateWizardData('duration', { completeSequence: true, endOnSide: null })}
                                  className="w-4 h-4 text-sage-600"
                                />
                                <span className="text-sm text-sage-700">
                                  Complete final repeat (all {repeatInfo.rowsInPattern} {construction === 'round' ? 'rounds' : 'rows'})
                                </span>
                              </label>

                              {/* Options 3+: End on specific side (only if safe) */}
                              {endOnSideOption && endOnSideOption.options.map(option => {
                                const needsExtraRow = option !== endOnSideOption.naturalEndingSide;
                                return (
                                  <label key={option} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name="endingBehavior"
                                      checked={wizardData.duration.endOnSide === option && !wizardData.duration.completeSequence}
                                      onChange={() => updateWizardData('duration', { completeSequence: false, endOnSide: option })}
                                      className="w-4 h-4 text-sage-600"
                                    />
                                    <span className="text-sm text-sage-700">
                                      End on {option} {construction === 'round' ? 'round' : ''}
                                      {needsExtraRow && (
                                        <span className="text-xs text-sage-500 ml-1">(+1 {construction === 'round' ? 'round' : 'row'})</span>
                                      )}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>

                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </label>
          )}

          {/* Color pattern repeats - only show if colorwork exists */}
          {hasColorPattern && (
            <label className={`block cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${wizardData.duration.type === 'color_repeats'
              ? 'border-sage-500 bg-sage-100 text-sage-700 shadow-sm'
              : 'border-wool-200 bg-white text-wool-700 hover:border-sage-300 hover:bg-sage-50'
              }`}>
              <div className="flex items-start gap-4">
                <input
                  type="radio"
                  name="duration_type"
                  value="color_repeats"
                  checked={wizardData.duration.type === 'color_repeats'}
                  onChange={() => handleDurationTypeSelect('color_repeats')}
                  className="w-4 h-4 text-sage-600 mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-2xl">🎨</div>
                    <div className="text-left">
                      <div className="font-semibold text-base">Repeat Color Pattern</div>
                      <div className="text-sm opacity-75">Repeat the stripe sequence</div>
                    </div>
                  </div>

                  {wizardData.duration.type === 'color_repeats' && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-sage-700">Repeat the color pattern</span>
                        <IncrementInput
                          value={wizardData.duration.value}
                          onChange={(value) => updateWizardData('duration', { value })}
                          label="color repeats"
                          unit="times"
                          min={1}
                          size="sm"
                        />
                      </div>

                      {wizardData.duration.value && (() => {
                        // Calculate total rows in stripe sequence
                        const totalRowsInSequence = wizardData.colorwork.stripeSequence.reduce(
                          (sum, stripe) => sum + (stripe.rows || 0),
                          0
                        );
                        const totalRows = totalRowsInSequence * parseInt(wizardData.duration.value);

                        return (
                          <div className="text-xs text-sage-600 bg-sage-50 rounded-lg p-2">
                            <strong>Preview:</strong> Work the {totalRowsInSequence}-{construction === 'round' ? 'round' : 'row'} stripe pattern {wizardData.duration.value} times
                            ({totalRows} total {construction === 'round' ? 'rounds' : 'rows'})
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </label>
          )}
        </div>
      )}

      {/* Save Actions for Edit Mode */}
      {showSaveActions && (
        <div className="pt-4 border-t border-wool-200">
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 btn-tertiary"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave()}
              className="flex-1 btn-primary"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DurationChoice;